/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css, html, LitElement } from 'lit-element';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { DateFormatHelper } from '../../utils/date-format-helper.js';
import { TABLET_BREAKPOINT } from '../../utils/tablet-breakpoint.js';
import { windowSize } from '../../utils/window-size.js';
import './sh-private-calendar-view.js';

/**
 * @prop {String} label - Defines the label of the datepicker input.
 * @attr {String} label - Defines the label of the datepicker input.
 * @prop {String} format - Defines the format of the date to be displayed in input.
 * Any combination of the parts `D` ,`DD`,`M`, `MM`,`MMM` and `YYYY` can be a format.
 * `D` and `M` stands for dates and months whose value if less than 10, will have only 1 character. eg: 2
 * Values greater than 10 can have two characters.
 * `DD` and `MM`  stands for dates and months whose value if less than 10, will have 2 characters. eg: 02 .
 * Values greater than 10 can have two characters.
 * `MMM` stands for month-name . It can have any number of characters in either the long form or short form of the month in the specified locale.
 * For more information about long form and short form of the month,
 * visit : https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#parameters
 * * examples of valid format : `MMDDYYYY`,`MMMDD`,`MMMYYYYDD` , etc
 * * examples of invalid format : `YYYYMYYYY`, `YYYYmmdd` , etc
 * @attr {String} format - Defines the format of the date to be displayed in input.
 * Any combination of the parts `D` ,`DD`,`M`, `MM`,`MMM` and `YYYY` can be a format.
 * `D` and `M` stands for dates and months whose value if less than 10, will have only 1 character. eg: 2
 * Values greater than 10 can have two characters.
 * `DD` and `MM`  stands for dates and months whose value if less than 10, will have 2 characters. eg: 02 .
 * Values greater than 10 can have two characters.
 * `MMM` stands for month-name . It can have any number of characters in either the long form or short form of the month in the specified locale.
 * For more information about long form and short form of the month,
 * visit : https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#parameters
 * * examples of valid format : `MMDDYYYY`,`MMMDD`,`MMMYYYYDD` , etc
 * * examples of invalid format : `YYYYMYYYY`, `YYYYmmdd` , etc
 *
 * @prop {String} separator - Defines the separator of the date to be displayed in input.
 * Separator can be any string of any length except
 *  * empty-space character(no-separator condition)
 *  * a mix of `-` and any other characters (including `-` itself) (eg `--` , `- `, are not allowed)
 *
 * Blank space character is allowed.
 * @attr {String} separator - Defines the separator of the date to be displayed in input.
 * Separator can be any string of any length except
 *  * empty-space character(no-separator condition)
 *  * a mix of `-` and any other characters (including `-` itself) (eg `--` , `- `, are not allowed)
 *
 * Blank space character is allowed.
 * @prop {Number} months - Defines the number of months to be shown in datepicker either 2 or 3 months.
 * @attr {Number} months - Defines the number of months to be shown in datepicker either 2 or 3 months.
 * @prop {Boolean} disabled - If set, won't allow mouse events and disabled styles will be applied to the datepicker input.
 * @attr {Boolean} disabled - If set, won't allow mouse events and disabled styles will be applied to the datepicker input.
 * @prop {Boolean} readonly - If true, datepicker cannot be modified.
 * @attr {Boolean} readonly - If true, datepicker cannot be modified.
 * @prop {Boolean} range - If set, allows to select date range (from and to date).
 * @attr {Boolean} range - If set, allows to select date range (from and to date).
 * @prop {Boolean} futureDateDisable - If true, future dates will be disabled.
 * @attr {Boolean} future-date-disable - If true, future dates will be disabled.
 * @prop {Boolean} pastDateDisable - If true, past dates will be disabled.
 * @attr {Boolean} past-date-disable - If true, past dates will be disabled.
 * @prop {Boolean} condensed - If set, then the condensed format of datepicker is used to display information.
 * @attr {Boolean} condensed - If set, then the condensed format of datepicker is used to display information.
 * @prop {Boolean} editable - If set to true, the user will be able to enter the date manually along with the selection in the calendar.
 * @attr {Boolean} editable - If set to true, the user will be able to enter the date manually along with the selection in the calendar.
 * @prop {String} responsive - defines if Datepicker will use mobile version or not.
 * @attr {String} responsive - defines if Datepicker will use alternate mobile version or not.
 * @prop {String} scrollpicker - In the mobile version, defines if Datepicker will use alternate scrollpicker view.
 * @attr {String} scrollpicker - In the mobile version, defines if Datepicker will use alternate scrollpicker view.
 * @prop {Boolean} error - If set to true, the datepicker  displays styles for invalid input.
 * @attr {Boolean} error - If set to true, the datepicker  displays styles for invalid input.
 * @prop {Boolean} success - If set to true, the datepicker  displays styles for valid input.
 * @attr {Boolean} success - If set to true, the datepicker  displays styles for valid input.
 * @prop {Boolean} safety - If set to true, the datepicker  displays styles for safety-relevant input (yellow outline).
 * @attr {Boolean} safety - If set to true, the datepicker  displays styles for safety-relevant input (yellow outline).
 * @prop {String} hint - Displays a message when error property is set to true and when error-message is not an empty string.
 * @attr {String} hint - Displays a message when error property is set to true and when error-message is not an empty string.
 * @prop {String} errorMessage - Displays a message when error property is set to true and when error-message is not undefined.
 * @attr {String} error-message - Displays a message when error property is set to true and when error-message is not undefined.
 * @prop {Boolean} noBorder - If set to true, the bottom border will not be displayed.
 * @attr {Boolean} no-border - If set to true, the bottom border will not be displayed
 * @prop {String} fromDate - The start date in a range value. This is a readonly property. Setting fromDate property will not reflect in value property.
 * @attr {String} from-date - The start date in a range value. This is a readonly attribute. Setting from-date attribute will not reflect in value property.
 * @prop {String} toDate - The end date in a range value. This is a readonly property. Setting toDate property will not reflect in value property.
 * @attr {String} to-date - The end date in a range value. This is a readonly attribute. Setting to-date attribute will not reflect in value property.
 * @prop {Boolean} active - Whether the datepicker overlay is opened or not.
 * @attr {Boolean} active - Whether the datepicker overlay is opened or not.
 * @prop {Boolean} mandatory - If set to true, sets an asterisk near to the label.
 * @attr {Boolean} mandatory - If set to true, sets an asterisk near to the label.
 * @prop {String} max - Dates above this date will be disabled. Has to be in the format YYYY-MM-DD.
 * @attr {String} max - Dates above this date will be disabled. Has to be in the format YYYY-MM-DD.
 * @prop {String} min - Dates below this date will be disabled. Has to be in the format YYYY-MM-DD.
 * @attr {String} min - Dates below this date will be disabled. Has to be in the format YYYY-MM-DD.
 * @prop {String} value - The value of the datepicker in the specified format.
 * @attr {String} value - The value of the datepicker in the specified format.
 * @prop {String} locale - The locale value used for formatting the month and week days in the datepicker views.
 * Locale **will not** change based on browser language automatically. This has to be done by the consumer.
 * @attr {String} locale - The locale value used for formatting the month and week days in the datepicker views.
 * Locale **will not** change based on browser language automatically. This has to be done by the consumer.
 * @prop {Array} standardValue - Array containing the standard Date object/objects of the value/values present in the `value` property. If the value is invalid or empty,
 * this will be an empty array.
 */

export class SHDatepicker extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      error: { type: Boolean, reflect: true },
      errorMessage: { type: String, reflect: true, attribute: 'error-message' },
      hint: { type: String, reflect: true, attribute: 'hint' },
      success: { type: Boolean, reflect: true },
      disabled: { type: Boolean, reflect: true },
      readonly: { type: Boolean, reflect: true },
      safety: { type: Boolean, reflect: true },
      mandatory: { type: Boolean, reflect: true },
      value: { type: String, reflect: true },
      active: { type: Boolean, reflect: true },
      range: { type: Boolean, reflect: true },
      separator: { type: String, reflect: true },
      format: { type: String, reflect: true },
      futureDateDisable: {
        type: Boolean,
        reflect: true,
        attribute: 'future-date-disable',
      },
      condensed: { type: Boolean, reflect: true },
      noBorder: { type: Boolean, reflect: true, attribute: 'no-border' },
      min: { type: String, reflect: true },
      max: { type: String, reflect: true },
      editable: { type: Boolean, reflect: true },
      months: { type: Number, reflect: true },
      pastDateDisable: {
        type: Boolean,
        reflect: true,
        attribute: 'past-date-disable',
      },
      responsive: {
        type: String,
        reflect: true,
      },
      scrollpicker: {
        type: String,
        reflect: true,
      },
      fromDate: {
        type: String,
        reflect: true,
        attribute: 'from-date',
      },
      toDate: {
        type: String,
        reflect: true,
        attribute: 'to-date',
      },
      locale: {
        type: String,
        reflect: true,
      },
      standardValue: { type: Array },
      _currentView: { type: String },
      _selectedDates: { type: Array },
      _todaysDate: { type: Object },
      _showOverlay: { type: Boolean },
      _month: { type: Number },
      _year: { type: Number },
      _minDate: { type: String },
      _maxDate: { type: String },
      _scrollpicker: { type: Boolean },
      _responsive: { type: Boolean },
      _scrollpickerItems: { type: Array },
      _scrollpickerScroll: { type: Array },
      _scrollpickerValues: { type: Array },
    };
  }
  static get styles() {
    return css`
      :host {
        display: block;
        width: 100%;
        position: relative;
      }
      /**
      Correcting the icon opacity to the
      idle state color of button icon so
      that the calendar icon is not so light
      when disabled property is applied to 
      datepicker
       */
      :host([disabled]) sh-icon[icon='calendar'] {
        color: rgba(var(--ui-1), var(--opacity-3));
      }
      .overlay-container {
        position: absolute;
        height: 2.5rem;
        top: 0px;
        left: 0px;
      }
      :host([condensed]) .overlay-container {
        height: 2rem;
      }
      .input {
        outline: none;
      }
    `;
  }

  constructor() {
    super();
    this.label = 'Label';
    this.format = 'MMDDYYYY';
    this.separator = '/';
    this.months = 1;
    this.disabled = false;
    this.readonly = false;
    this.range = false;
    this.futureDateDisable = false;
    this.pastDateDisable = false;
    this.condensed = false;
    this.editable = false;
    this.responsive = 'false';
    this.scrollpicker = 'false';
    this.error = false;
    this.success = false;
    this.safety = false;
    this.noBorder = false;
    this.mandatory = false;
    this.max = '';
    this.min = '';
    this.locale = 'en';
    this.standardValue = [];
    this._selectedDates = [];
    this._scrollpickerValues = [];
  }

  render() {
    return html`<sh-input-text
        .label="${this.label}"
        .error="${this.error}"
        .errorMessage="${this.errorMessage}"
        .hint="${this.hint}"
        .success="${this.success}"
        .disabled="${this.disabled}"
        .safety="${this.safety}"
        .mandatory="${this.mandatory}"
        .readonly="${this.readonly}"
        .value="${this.value}"
        ._inputReadonly="${!this.editable}"
        ._showOutline="${this.active && !this.readonly}"
        .condensed="${this.condensed}"
        .noBorder="${this.noBorder}"
        ._notifyInputClick="${true}"
        ._notifyClearClick="${true}"
        @input-clicked="${(e) => {
          e.stopPropagation();
          this._toggleActive();
        }}"
        @input="${(e) => {
          this.active = false;
          this.value = e.target.value;
        }}"
        @clear-clicked="${(e) => {
          e.stopPropagation();
          this.value = '';
          if (!this.active && !this.editable) {
            e.target.defocus();
          } else {
            this.active = false;
          }
        }}"
        class="input"
      >
        ${!this.readonly
          ? html`<sh-icon
              slot="icon"
              icon="calendar"
              size="s"
              button
              @click="${() => {
                this._toggleActive();
              }}"
              .disabled="${this.disabled}"
            ></sh-icon>`
          : ''}
      </sh-input-text>
      <div class="overlay-container">
        ${!this._responsive
          ? html`
              <sh-overlay
                .opened=${this._showOverlay && !this.readonly}
                scroll-action="refit"
                no-overlap
                horizontal-align="auto"
                auto-fit-on-attach
                .noCancelOnOutsideClick="${true}"
              >
                ${this._showOverlay
                  ? html`<sh-private-calendar-view
                      .currentView="${this._currentView}"
                      .months="${this.months}"
                      .startingMonth="${this._month}"
                      .startingYear="${this._year}"
                      .todaysDate="${this._todaysDate}"
                      .selectedDates="${this._selectedDates}"
                      .futureDateDisable="${this.futureDateDisable}"
                      .minDate="${this._minDate}"
                      .maxDate="${this._maxDate}"
                      .range="${this.range}"
                      .pastDateDisable="${this.pastDateDisable}"
                      .attachNode="${this}"
                      .locale="${this.locale}"
                      @ready-to-update-value="${(e) => {
                        e.stopPropagation();
                        this._updateValue(e.detail.selectedDates);
                        e.target.close = true; // to have that fade-out animation before destroying this DOM
                      }}"
                      @calendar-closed="${(e) => {
                        e.stopPropagation();
                        e.target.close = true; // to have that fade-out animation before destroying this DOM
                      }}"
                      @animationend="${(e) => {
                        e.stopPropagation();
                        if (e.target.close) {
                          this.active = false;
                        }
                      }}"
                    ></sh-private-calendar-view>`
                  : ''}
              </sh-overlay>
            `
          : ''}
      </div>
      ${this._responsive && this._showOverlay && !this.readonly
        ? this._scrollpicker
          ? html`<sh-scrollpicker
              .items="${this._scrollpickerItems}"
              .visible="${this._showOverlay}"
              .scroll="${this._scrollpickerScroll}"
              .values="${this._scrollpickerValues}"
              @values-changed="${(e) => {
                e.stopPropagation();
                this._updateValueFromScrollPicker(e.target);
              }}"
              @visible-changed="${(e) => {
                e.stopPropagation();
                if (!e.target.visible) {
                  this.active = false;
                }
              }}"
              @scroll-changed="${(e) => {
                e.stopPropagation();
                this._scrollpickerScroll = e.target.scroll;
              }}"
            ></sh-scrollpicker>`
          : html`<sh-drawer
              .visible="${this._showOverlay && !this.readonly}"
              position="bottom"
              @transitionend="${(e) => {
                e.stopPropagation();
                if (!e.target.visible) {
                  this.active = false;
                }
              }}"
              class="initial-slide-on-connect"
            >
              <sh-private-calendar-view
                .currentView="${this._currentView}"
                .months="${this.months}"
                .startingMonth="${this._month}"
                .startingYear="${this._year}"
                .todaysDate="${this._todaysDate}"
                .selectedDates="${this._selectedDates}"
                .futureDateDisable="${this.futureDateDisable}"
                .minDate="${this._minDate}"
                .maxDate="${this._maxDate}"
                .range="${this.range}"
                .pastDateDisable="${this.pastDateDisable}"
                .attachNode="${this}"
                .responsive="${true}"
                .locale="${this.locale}"
                @ready-to-update-value="${(e) => {
                  e.stopPropagation();
                  this._updateValue(e.detail.selectedDates);
                  // to trigger the transitionend of the drawer component to have that fade-out-slide-out effect.
                  // and here we don't need the calendar-closed event since the drawer itself will detect outside clicks.
                  e.target.parentElement.visible = false;
                }}"
              ></sh-private-calendar-view>
            </sh-drawer>`
        : ''}`;
  }
  connectedCallback() {
    super.connectedCallback();
    if (
      windowSize.util.valueMatches(document.documentElement.clientWidth, '<', TABLET_BREAKPOINT)
    ) {
      this.responsive = 'true';
    }
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('active')) {
      this._handleActive();
    }
    if (changedProperties.has('min')) {
      this._handleMin();
    }
    if (changedProperties.has('max')) {
      this._handleMax();
    }
    if (changedProperties.has('scrollpicker')) {
      this._scrollpicker = JSON.parse(this.scrollpicker);
    }
    if (changedProperties.has('responsive')) {
      this._responsive = JSON.parse(this.responsive);
    }
    if (changedProperties.has('value')) {
      this._setResetFromToDates();
      if (!this.value) {
        this._clearSelectedDates();
      }
    }
    dispatchAttributeChangedEvent(this, changedProperties, [
      'active',
      'value',
      'error',
      'disabled',
    ]);
  }

  _handleMin() {
    if (!this.min) {
      this._minDate = null;
    } else {
      const todaysDate = this._getTodaysDate();
      const minDateIsValid = DateFormatHelper.isValidDate(
        this.min,
        'YYYYMMDD',
        '-',
        'en',
        todaysDate
      );
      if (minDateIsValid) {
        this._minDate = DateFormatHelper.parseDate(this.min, 'YYYY-MM-DD', '-', 'en', todaysDate);
      } else {
        this._minDate = null;
      }
    }
  }

  _handleMax() {
    if (!this.max) {
      this._maxDate = null;
    } else {
      const todaysDate = this._getTodaysDate();
      const maxDateIsValid = DateFormatHelper.isValidDate(
        this.max,
        'YYYYMMDD',
        '-',
        'en',
        todaysDate
      );
      if (maxDateIsValid) {
        this._maxDate = DateFormatHelper.parseDate(this.max, 'YYYY-MM-DD', '-', 'en', todaysDate);
      } else {
        this._maxDate = null;
      }
    }
  }

  _updateValue(selectedDates) {
    if (selectedDates.length === 2) {
      this.value = `${DateFormatHelper.getFormattedDate(
        selectedDates[0],
        this.format,
        this.separator,
        this.locale
      )}-${DateFormatHelper.getFormattedDate(
        selectedDates[1],
        this.format,
        this.separator,
        this.locale
      )}`;
    } else {
      this.value = `${DateFormatHelper.getFormattedDate(
        selectedDates[0],
        this.format,
        this.separator,
        this.locale
      )}`;
    }
  }
  _setMonthYearDate(date) {
    this._month = date.getMonth();
    this._year = date.getFullYear();
    this._scrollpickerDate = date.getDate();
  }
  _setDefaultVariables(todaysDate) {
    this._setMonthYearDate(todaysDate);
    this._selectedDates = [];
  }
  _setSingleDateVariables(singleDate) {
    this._setMonthYearDate(singleDate);
    this._selectedDates = [singleDate];
  }
  _setRangeDateVariables(rangeDates) {
    this._setMonthYearDate(rangeDates[1]);
    this._selectedDates = rangeDates;
  }
  _updateVariables() {
    this._todaysDate = this._getTodaysDate();
    this._currentView = 'date';
    if (this.value) {
      const actualFormat = DateFormatHelper.insertSeperator(this.separator, this.format);
      const isValueValid = DateFormatHelper.isValueValid(
        this.value,
        this.range,
        this.separator,
        this.format,
        this.futureDateDisable,
        this.pastDateDisable,
        this._todaysDate,
        this.min,
        this.max,
        this.locale
      );
      if (isValueValid) {
        if (!this.range) {
          const parsedDate = DateFormatHelper.parseDate(
            this.value,
            actualFormat,
            this.separator,
            this.locale,
            this._todaysDate
          );
          this._setSingleDateVariables(parsedDate);
        } else {
          const parsedRangeDates = DateFormatHelper.parseRangeDates(
            this.value,
            actualFormat,
            this.separator,
            this.locale,
            this._todaysDate
          );
          this._setRangeDateVariables(parsedRangeDates);
        }
      } else {
        this._setDefaultVariables(this._todaysDate);
      }
    } else {
      this._setDefaultVariables(this._todaysDate);
    }
  }

  _getTodaysDate() {
    const todaysDate = new Date();
    todaysDate.setHours(0, 0, 0, 0);
    return todaysDate;
  }

  _toggleActive() {
    this.active = !this.active;
  }

  _handleActive() {
    if (this.active) {
      this._updateVariables();
      this._showOverlay = true;
      if (this._scrollpicker) {
        this._initialiseScrollpicker();
      }
    }
    if (!this.active) {
      if (this.editable || this.value) {
        this.shadowRoot.querySelector('sh-input-text').focus();
      } else if (!this.value) {
        this.shadowRoot.querySelector('sh-input-text').defocus();
      }
      this._showOverlay = false;
      this._scrollpickerItems = [];
      this._scrollpickerValues = [];
      this._scrollpickerScroll = false;
      // clearing the selected dates after
      // every closing of overlay.
      // since the selected Dates will
      // be updated again when it is opened
      // from the value property.
      this._clearSelectedDates();
    }
  }
  /**
   * Scrollpicker Code
   */
  /* The basic initialisation code */
  _initialiseScrollpicker() {
    const months = new Array(12)
      .fill(0)
      .map((_, index) => DateFormatHelper.getShortMonthName(index, this.locale));
    const years = new Array(201).fill(0).map((_, index) => index + 1900);
    const dayCount = this._getDayCountforMonthYear(this._month, this._year);
    const days = new Array(dayCount).fill(0).map((_, index) => index + 1);
    this._scrollpickerItems = [months, days, years];
    this._scrollpickerValues = [
      DateFormatHelper.getShortMonthName(this._month, this.locale),
      this._scrollpickerDate,
      this._year,
    ];
    this._scrollpickerScroll = true;
  }
  _getDayCountforMonthYear(monthIndex, year) {
    let dayCount = 0;
    for (let i = 1; i <= 31; i++) {
      let testDate = new Date(year, monthIndex, i, 0, 0, 0, 0);
      if (testDate.getMonth() === monthIndex) {
        ++dayCount;
      } else {
        break;
      }
    }
    return dayCount;
  }
  _updateValueFromScrollPicker(scrollpicker) {
    const values = scrollpicker.values;
    const monthIndex = DateFormatHelper.getMonthIndex(values[0], this.locale);
    const year = values[2];
    const date = values[1];
    const dayCount = this._getDayCountforMonthYear(monthIndex, year);
    const dayCountIsNotCorrect = dayCount !== scrollpicker.items[1].length;
    const requiredDate = dayCountIsNotCorrect ? (date > dayCount ? dayCount : date) : date; // if date is 31 and only 30 days are present in that month, then we correct to 30th instead of 31st.
    /**
     * _______________________________________________________________
     *
     * Case 1:
     * _______________________________________________________________
     * Assume this function was called when changing month from Nov to
     * Dec and the date was 30 and year was 2021
     *
     * So the current scrollpicker values will be Dec 30 2021.
     *
     * Then we check if the days are same after the scroll has
     * happened, so that if it is December and the current
     * dayCount is 30 (which it will be since previous month was Nov),
     * it is wrong. So to correct it, we set the values
     * of scrollpicker and value of this datepicker to Dec 30 2021.
     * Then we make scrollpicker scroll to that value incase if the current scroll position is wrong
     * by setting the scrollpickerScroll property to true.
     * After this function is executed the update cycle of datepicker is
     * triggered since we changed three properties of datepicker (value and scrollpickerValues and scrollpickerScroll).
     * This forces the render to happen again setting the previously set scrollpickerValues
     * and scroll property to the scrollpicker. This forces an update cycle of scrollpicker since "scroll" property is changed to true from false
     * where it will trigger the observer for the scroll boolean property.
     * The scroll boolean of scrollpicker will wait for the scrollpicker's update and render to finish
     * so that we wait for any unnecessary scroll due to change of list item length of a scroll-container
     * and after that it is made to scroll to the selected values
     * so that there is no chance of changing values unnecessarily.
     *
     * __________________________________________________________________
     *
     * Case 2:
     * __________________________________________________________________
     * Assume this function was called when changing month from Dec to
     * Nov and the date was 31 and year was 2021
     *
     * So the current scrollpicker values will be Nov 31 2021.
     *
     * Then we check if the days are same after the scroll has
     * happened, so that if it is November and the current
     * dayCount is 31 (which it will be since previous month was Dec),
     * it is wrong. So to correct it, we set the values
     * of scrollpicker and value of this datepicker to Nov 30 2021.
     *
     * Here we arrived at Nov 30 2021 by correcting the date to the max dayCount
     * if it exceeds the dayCount and retaining all other parameters
     *
     * Then we make scrollpicker scroll to that value incase if the current scroll position is wrong
     * by setting the scrollpickerScroll property to true.
     * After this function is executed the update cycle of datepicker is
     * triggered since we changed three properties of datepicker (value and scrollpickerValues and scrollpickerScroll).
     *
     * This forces the render to happen again setting the previously set scrollpickerValues
     * and scroll property to the scrollpicker.
     *
     * This forces an update cycle of scrollpicker since "scroll" property is changed to true from false
     * and values is also changed where it will trigger the observer for the values property and dispatch event again.
     * This change is captured again by this function and the values are now Nov 30 2021.
     *
     * The dayCount is same. so it will exit this function and then go back to the updated of scrollpicker
     * where it will continue executing to execute the observer for scroll property (which we set in previous cycle)
     * then it will scroll to the values (by this time, the scrollpicker dom is as expected since it went through some time here)
     * and then reset scroll to false.
     *
     * then updated cycle again happens in scrollpicker(due to setting values property in previous step) where it checks if the
     * new and old values are same or not. Since they are same, no more values-changed event is fired. The scrollpickerScroll property
     * is reset to false since an event binding is present.
     *
     */
    this._scrollpickerValues = [
      DateFormatHelper.getShortMonthName(monthIndex, this.locale),
      requiredDate,
      year,
    ];
    this.value = DateFormatHelper.getFormattedDate(
      new Date(year, monthIndex, requiredDate, 0, 0, 0, 0),
      this.format,
      this.separator,
      this.locale
    );
    if (dayCountIsNotCorrect) {
      const days = new Array(dayCount).fill(0).map((_, index) => index + 1);
      this._scrollpickerItems[1] = days;
      this._scrollpickerScroll = true;
    }
  }
  _resetFromToDate() {
    this.fromDate = null;
    this.toDate = null;
  }
  _setResetFromToDates() {
    this._todaysDate = this._getTodaysDate();
    let standardValue = [],
      fromDate = null,
      toDate = null;
    if (this.value) {
      const actualFormat = DateFormatHelper.insertSeperator(this.separator, this.format);
      const isValueValid = DateFormatHelper.isValueValid(
        this.value,
        this.range,
        this.separator,
        this.format,
        this.futureDateDisable,
        this.pastDateDisable,
        this._todaysDate,
        this.min,
        this.max,
        this.locale
      );
      if (isValueValid) {
        if (this.range) {
          [fromDate, toDate] = DateFormatHelper.extractFromToParts(
            this.value,
            this.separator,
            actualFormat
          );
          const parsedRangeDates = DateFormatHelper.parseRangeDates(
            this.value,
            actualFormat,
            this.separator,
            this.locale,
            this._todaysDate
          );
          standardValue = parsedRangeDates;
        } else {
          const parsedDate = DateFormatHelper.parseDate(
            this.value,
            actualFormat,
            this.separator,
            this.locale,
            this._todaysDate
          );
          standardValue = [parsedDate];
        }
      }
    }
    this.fromDate = fromDate;
    this.toDate = toDate;
    this.standardValue = standardValue;
  }
  _clearSelectedDates() {
    this._selectedDates = [];
  }
}
customElements.define('sh-datepicker', SHDatepicker);
